import { useEffect, useRef, useState } from "react";
import Notice from "~/components/shared/Notice/Notice";

type SchedulerError = {
  type: string;
  message: string;
};

type SchedulerStatusResponse = {
  scheduled: boolean;
  error?: SchedulerError;
};

type Props = {
  job: {
    id: string;
    state: string;
    basePath?: string;
    hosted?: boolean;
  };
};

const SchedulerErrorNotice = ({ job }: Props) => {
  const [error, setError] = useState<SchedulerError | null>(null);
  const intervalRef = useRef<number | null>(null);

  const shouldPoll = job.state === "scheduled" && job.hosted === true;

  const fetchSchedulerStatus = async (): Promise<void> => {
    if (!job.basePath) {
      return;
    }

    try {
      const response = await fetch(`${job.basePath}/hosted_agent_status`, {
        headers: { Accept: "application/json" },
      });

      if (response.ok) {
        const data: SchedulerStatusResponse = await response.json();
        setError(data.error || null);
      } else {
        setError(null);
      }
    } catch {
      setError(null);
    }
  };

  useEffect(() => {
    if (!Features.ShowErrorNoticesForHostedAgents || !shouldPoll) {
      setError(null);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Initial fetch
    fetchSchedulerStatus();

    // Set up polling with jitter (25-35 seconds)
    const interval = 25000 + Math.random() * 10000;
    intervalRef.current = window.setInterval(fetchSchedulerStatus, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [shouldPoll, job.basePath]);

  // Check if feature flag is enabled
  if (!Features.ShowErrorNoticesForHostedAgents) {
    return null;
  }

  if (!shouldPoll) {
    return null;
  }

  if (!error) {
    return null;
  }

  // Use error type for unknown errors, warning for known errors
  const isUnknownError = error.type !== "CONCURRENCY_LIMIT" && error.type !== "RESOURCE_LIMIT";
  const noticeType = isUnknownError ? "error" : "warning";

  return (
    <Notice
      title={error.message}
      noticeType={noticeType}
      icon="heroicons/16/solid/exclamation-triangle"
    />
  );
};

export default SchedulerErrorNotice;
